// A Gambit game can have a wagering bonus, a Cashback on Total Stake bonus that Bede activates on
// a deposit and completes, straight away, once the player has staked its target. An activation
// runs for 24 hours, so it can cross midnight, and a deposit while one is running doesn't start
// another. The bonus stays in the bonus list: unclaimable while an activation runs, and claimable
// again once it has ended, met or expired, and the day's one activation is free. So a claimable
// bonus can still have a met activation waiting to be spun for: what counts is the newest
// activation, which is in the player's bonus history, one record per activation, which
// has its own id, the bonus's as bonusId (and its promoCode, but no internalName), and the
// player's currentAccumulation, targetAccumulation, endDateToQualify, status (PartQualified while
// staking, Completed once met) and completedDate. usePromotions keeps the recent ones in the
// store (cashbackActivations) for useWageringProgress, and the newest one is the one that counts.
export const CASHBACK_ON_TOTAL_STAKE = "CashbackOnTotalStake";

// Bede marks the stake met as Completed, and dates it in completedDate; Rewarded and Fulfilled
// are what it could move on to.
const UNLOCKED_STATUSES = ["Completed", "Rewarded", "Fulfilled"];
const ENDED_STATUSES = ["Expired", "NotQualified", "Cancelled", "Revoked", "OptedOut"];
const STATUSES = ["Active", "PartQualified", ...UNLOCKED_STATUSES, ...ENDED_STATUSES];

// How far back the activations are fetched. A met activation's spin waits until it's claimed, so
// this is how long the site can still see it: a player back after longer is offered a deposit.
export const ACTIVATIONS_LOOK_BACK_DAYS = 30;

// Activations from the last ACTIVATIONS_LOOK_BACK_DAYS, ended ones included, so that the newest
// is always among them whatever order Bede lists them in (a wheel played every day has one a day),
// and an earlier day's completed one can't stand in for an expired one today. `activatedSince` is
// YYYYMMDD, as the bonus history modal sends it.
export const activationsQuery = (activatedSince) =>
    `?promoStatuses=${STATUSES.join(",")}&bonusTypes=${CASHBACK_ON_TOTAL_STAKE}&activatedDateStart=${activatedSince}&take=100`;

// Whether the player has a wagering bonus (internalName starting with `prefix`), whose activations
// the game needs.
export const needsActivations = (promotions, prefix) => (promotions ?? []).some(promotion =>
    promotion?.bonusType === CASHBACK_ON_TOTAL_STAKE && promotion.internalName?.toUpperCase().startsWith(prefix));

// The player's latest activation of the wagering bonus `bonus`: a record whose bonusId is the
// bonus's id, or failing that with its promoCode.
export const findActivation = (items, bonus) => {
    const isActivation = (item) => item?.bonusType === CASHBACK_ON_TOTAL_STAKE
        && ((bonus?.id && item.bonusId === bonus.id) || (bonus?.promoCode && item.promoCode === bonus.promoCode));
    const activated = (item) => Date.parse(item.activationDate ?? item.optInDate) || 0;

    return (items ?? []).filter(isActivation).sort((a, b) => activated(b) - activated(a))[0] ?? null;
};

// Bede's activationDate and completedDate are UTC without the Z (its activedDate has one), which
// Date.parse would read as the device's local time.
const parseUtc = (date) => (typeof date === "string" && !/(Z|[+-]\d\d:?\d\d)$/.test(date) ? Date.parse(`${date}Z`) : Date.parse(date)) || null;

// What the game makes of the newest activation. `state` is one of:
//   notStarted (none: the player has a deposit to make), wagering (with current, target and
//   remaining stake, and endsAt), unlocked, ended
// with the activation's id as activationId, Bede's status for it as status, and when it started and
// was met (ms, or null) as activatedAt and completedAt.
export const toProgress = (activation) => {
    if (!activation) return {state: "notStarted"};

    const target = Number(activation.targetAccumulation) || 0;
    const current = Math.min(Number(activation.currentAccumulation) || 0, target);
    let state = "wagering";
    if (UNLOCKED_STATUSES.includes(activation.status) || activation.completedDate) state = "unlocked";
    if (ENDED_STATUSES.includes(activation.status)) state = "ended";

    return {
        state,
        activationId: activation.id,
        status: activation.status ?? null,
        current,
        target,
        remaining: target - current,
        endsAt: activation.endDateToQualify ?? null,
        activatedAt: parseUtc(activation.activationDate ?? activation.optInDate),
        completedAt: parseUtc(activation.completedDate),
    };
};

// Campaign Manager's day, which its once-a-day limits go by.
const CAMPAIGN_TIME_ZONE = "Europe/London";
const campaignDay = (time) => new Date(time).toLocaleDateString("en-GB", {timeZone: CAMPAIGN_TIME_ZONE});

// Whether a met activation's spin has waited long enough to be claimed for the player
// (useClaimWaitingSpin). Until its prize is claimed the player stays in both segments, so a deposit
// then would start a go the wheel can't follow. That's possible once the day the activation began
// is over, as Campaign Manager lets a player in once a day. It also has to have been met at least
// `graceMs` ago (the brand's waitingSpinGraceHours, 3 by default), so a player who meets the stake
// late in the evening or just after midnight still gets to spin; a deposit in that time can start a
// go that hides the spin, as before the claim existed. Without a grace it's never overdue.
export const isSpinOverdue = (progress, graceMs, now = Date.now()) => {
    if (progress?.state !== "unlocked" || !progress.activatedAt || !progress.completedAt || !Number.isFinite(graceMs)) return false;

    return campaignDay(progress.activatedAt) !== campaignDay(now) && now - progress.completedAt >= graceMs;
};
